import config from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/Button';
import styles from '../style/organizationList.module.css';
import { Building } from "lucide-react";

const OrganizationList = () => {
  const { authData, userData } = useAuth();
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('A-Z');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await fetch(
          `${config.BASE_URL}/organizations`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) throw new Error('Failed to fetch organizations');

        const data = await response.json();

        // Filter organizations based on user role
        if (userData?.role === 'SUPERADMIN') {
          // SUPERADMIN can see all organizations
          setOrganizations(data);
        } else if (userData?.role === 'ADMIN' || userData?.role === 'USER') {
          // ADMIN/USER can only see their organization
          const filteredOrgs = data.filter(
            (org) => org.id === userData.organizationId
          );
          setOrganizations(filteredOrgs);
        } else {
          // Default: No organizations for unauthorized roles
          setOrganizations([]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [authData.accessToken, userData?.role, userData?.organizationId]);

  const handleOrganizationClick = (orgId, orgName) => {
    navigate(`/organization/${orgId}`, {
      state: { orgId, orgName },
    });
  };

  const handleAddOrganization = () => {
    navigate('/create-organization');
  };

  const filteredOrganizations = organizations
    .filter(
      (org) =>
        org.name.toLowerCase().includes(search.toLowerCase()) &&
        (!showActiveOnly || org.active)
    )
    .sort((a, b) =>
      sortOrder === 'A-Z'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );

  return (
    <div className={styles.organizationList_outerContainer}>
      <div className={styles.organizationList_header}>
        <input
          type="text"
          className={styles.organizationList_searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for..."
        />
        <Button
          color={search ? "#006d7d" : "gray"}
          text="Clear"
          onClick={() => setSearch('')}
          className={styles.organizationList_clearBtn}
        />
        <div className={styles.organizationList_sort}>
          <span className={styles.organizationList_sortLabel}>Sort By:</span>
          <select
            className={styles.organizationList_sortSelect}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="A-Z">A → Z</option>
            <option value="Z-A">Z → A</option>
          </select>
        </div>
        <div className={styles.organizationList_toggle}>
          <span className={styles.organizationList_toggleLabel}>Show All</span>
          <label className={styles.organizationList_toggleSlider}>
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={() => setShowActiveOnly(!showActiveOnly)}
            />
            <span className={styles.organizationList_sliderRound} />
          </label>
          <span className={styles.organizationList_toggleLabel}>Active Only</span>
        </div>
        {userData?.role === 'SUPERADMIN' && (
          <Button
            color="#006d7d"
            text="Add Organization"
            onClick={handleAddOrganization}
            className={styles.organizationList_addBtn}
          />
        )}
      </div>
      
      {loading ? (
        <div className={styles.organizationList_loadingContainer}>
          <div className={styles.organizationList_loader}></div>
          <p>Loading organizations...</p>
        </div>
      ) : error ? (
        <div className={styles.organizationList_errorContainer}>Error: {error}</div>
      ) : (
        <div className={styles.organizationList_list}>
          {filteredOrganizations.length > 0 ? (
            filteredOrganizations.map((org) => (
              <div
                key={org.id}
                className={styles.organizationList_listItem}
                onClick={() => handleOrganizationClick(org.id, org.name)}
              >
                <div className={styles.organizationList_icon}>
                <Building size={40} />

                </div>
                <div className={styles.organizationList_content}>
                  <div className={styles.organizationList_name}>{org.name}</div>
                  <div className={styles.organizationList_details}>
                    {org.type}  {org.country}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.organizationList_noAccountsContainer}>
              No organizations found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationList;